import { AnalysisMarkdown, AnalysisSubHeader, PriorityPill } from '../../../components';
import type { TimelyOpportunity } from '../CultureContext.types';
import { useTranslation } from 'react-i18next';

import { hasContent } from '../../../utils/hasContent';

interface TimelyOpportunitiesProps {
  timelyOpportunities?: TimelyOpportunity[];
}

/** The opportunities this block renders: those with a title or a description. */
export const withTimelyContent = (timelyOpportunities: TimelyOpportunity[] | undefined) =>
  (timelyOpportunities ?? []).filter(
    (opportunity) => hasContent(opportunity.title) || hasContent(opportunity.description),
  );

export const TimelyOpportunities = ({ timelyOpportunities }: TimelyOpportunitiesProps) => {
  const { t } = useTranslation('analysis');

  const opportunities = withTimelyContent(timelyOpportunities);
  if (!opportunities.length) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.timelyOpportunities.timelyOpportunities')}
        subtitle={t('analysisResult.timelyOpportunities.culturalMomentsReadyForActivation')}
        subtitleTone="meta"
      />
      <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-2">
        {opportunities.map((opportunity, index) => (
          <div
            key={opportunity.title ?? index}
            className="flex h-full flex-col gap-3 rounded-lg border border-border-light p-xl"
          >
            {opportunity.urgency && <PriorityPill priority={opportunity.urgency} />}
            {opportunity.title && (
              <span className="text-body-xl font-medium text-foreground">{opportunity.title}</span>
            )}
            {opportunity.description && (
              <AnalysisMarkdown
                text={opportunity.description}
                className="text-body-md text-foreground-alt"
              />
            )}
            {opportunity.timeline && (
              <AnalysisMarkdown
                text={opportunity.timeline}
                className="text-body-md text-muted-foreground"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
